import React, { Fragment, useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon, DocumentTextIcon, PrinterIcon } from '@heroicons/react/outline';
import { useLanguage } from '../contexts/LanguageContext';
import ErrorDisplay from './ErrorDisplay';
import api from '../services/api';

export default function DocumentViewerModal({ isOpen, onClose, policy, format }) {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [documentType, setDocumentType] = useState(null);
  const iframeRef = useRef(null);

  const handleViewDocument = async () => {
    if (!policy || !format) return;
    
    setError(null);
    setIsLoading(true);
    try {
      const response = await api.post(
        `/api/documents/view-from-policy/${policy.id}`,
        { format },
        {
          responseType: 'blob'
        }
      );

      const blob = response.data;
      const url = URL.createObjectURL(blob);
      setDocumentUrl(url);
      
             // Determine document type from response headers
      const contentType = response.headers['content-type'];
       if (contentType && contentType.includes('text/html')) {
         setDocumentType('html');
       } else if (contentType && contentType.includes('image')) {
         setDocumentType('image');
       } else if (contentType && contentType.includes('pdf')) {
         setDocumentType('pdf');
       } else {
         setDocumentType('docx');
       }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Document viewing failed:', error);
      setError(error);
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setDocumentUrl(null);
      setDocumentType(null);
      setError(null);
      onClose();
    }
  };

  const handlePrint = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.print();
    }
  };

  const getTemplateNames = () => {
    if (!policy) return { a4: '', a5: '' };
    
    if (policy.policyType === 'Mandatory Car Insurance') {
      return {
        a4: 'contract1a4.docx',
        a5: 'contract1a5.docx'
      };
    } else {
      return {
        a4: 'contacta4.docx',
        a5: 'contracta5.docx'
      };
    }
  };

  const templates = getTemplateNames();

  // Auto-trigger document viewing when modal opens
  useEffect(() => {
    if (isOpen && policy && format && !documentUrl && !isLoading) {
      handleViewDocument();
    }
  }, [isOpen, policy, format]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative bg-white rounded-lg shadow-xl transform transition-all sm:max-w-6xl sm:w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DocumentTextIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                        {t('documentViewer.title')}
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        {t('documentViewer.policyNumber')}: {policy?.policyNumber} - {t('documentViewer.format')}: {format?.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  
                                     <div className="flex items-center space-x-2">
                    {/* Print button removed - printing disabled in preview mode */}
                    <button
                      type="button"
                      className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      <span className="sr-only">Close</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="relative">
                  {error && (
                    <div className="p-4">
                      <ErrorDisplay 
                        error={error} 
                        onDismiss={() => setError(null)} 
                        className="mb-4"
                      />
                    </div>
                  )}

                  {isLoading && (
                    <div className="flex items-center justify-center p-8">
                      <div className="text-center">
                        <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-600">{t('documentViewer.generating')}</p>
                      </div>
                    </div>
                  )}

                                     {documentUrl && !isLoading && (
                     <div className="w-full h-96 sm:h-[600px]">
                       {documentType === 'html' ? (
                         <iframe
                           ref={iframeRef}
                           src={documentUrl}
                           className="w-full h-full border-0"
                           title="Document Viewer"
                         />
                       ) : documentType === 'image' ? (
                         <div className="w-full h-full flex items-center justify-center bg-gray-50 overflow-auto">
                           <img
                             src={documentUrl}
                             alt="Document"
                             className="max-w-full max-h-full object-contain"
                             style={{ maxWidth: '100%', maxHeight: '100%' }}
                           />
                         </div>
                       ) : documentType === 'pdf' ? (
                         <iframe
                           ref={iframeRef}
                           src={documentUrl}
                           className="w-full h-full border-0"
                           title="Document Viewer"
                         />
                       ) : (
                         <div className="w-full h-full flex items-center justify-center bg-gray-50">
                           <div className="text-center">
                             <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                             <p className="text-gray-600 mb-4">{t('documentViewer.docxNotSupported')}</p>
                             <a
                               href={documentUrl}
                               download
                               className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                             >
                               {t('documentViewer.downloadDocument')}
                             </a>
                           </div>
                         </div>
                       )}
                     </div>
                   )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
} 